import { RefObject, useEffect, useState } from 'react';

interface UseIsInViewOptions extends IntersectionObserverInit {
  /** Whether the observer is disabled */
  disabled?: boolean;
}

export type InViewPosition = 'above' | 'below' | 'inView' | null;

const useIsInView = (
  ref: RefObject<HTMLElement>,
  options: UseIsInViewOptions = {},
) => {
  const [isInView, setIsInView] = useState(false);
  const [position, setPosition] = useState<InViewPosition>(null);

  useEffect(() => {
    if (!ref.current || options.disabled) return;

    const observer = new IntersectionObserver(([entry]) => {
      const isIntersecting = entry.isIntersecting;

      setIsInView(isIntersecting);

      if (isIntersecting) {
        setPosition('inView');
      } else {
        const top = entry.boundingClientRect.top;

        if (top < 0) {
          setPosition('above');
        } else if (top > 0) {
          setPosition('below');
        } else {
          setPosition(null);
        }
      }
    }, options);

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [ref, options]);

  return { isInView, position };
};

export default useIsInView;
